import { useEffect, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faSpinner,
  faCircleCheck,
  faCircleXmark,
} from '@fortawesome/free-solid-svg-icons'
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { cn } from '../../shared/utils/cn'

export type SendMode = 'direct' | 'pipeline'

export interface SendOutcome {
  recipient: string
  detail?: string
}

interface TestSendFormProps {
  mode: SendMode
  recipientLabel: string
  recipientType: 'email' | 'tel'
  recipientPlaceholder: string
  recipientRegex: RegExp
  recipientInvalidMessage: string
  bodyRows: number
  defaultBody: string
  storageKey: string
  buttonLabel: string
  buttonIcon: IconDefinition
  onSend: (params: { to: string; body: string }) => Promise<SendOutcome>
  bodyHelper?: React.ReactNode
}

type Status =
  | { kind: 'idle' }
  | { kind: 'sending' }
  | { kind: 'success'; outcome: SendOutcome }
  | { kind: 'error'; message: string }

export function TestSendForm({
  mode,
  recipientLabel,
  recipientType,
  recipientPlaceholder,
  recipientRegex,
  recipientInvalidMessage,
  bodyRows,
  defaultBody,
  storageKey,
  buttonLabel,
  buttonIcon,
  onSend,
  bodyHelper,
}: TestSendFormProps) {
  const [to, setTo] = useState(() => {
    const saved = localStorage.getItem(storageKey) ?? ''
    return recipientRegex.test(saved.trim()) ? saved : ''
  })
  const [body, setBody] = useState(defaultBody)
  const [touched, setTouched] = useState(false)
  const [status, setStatus] = useState<Status>({ kind: 'idle' })

  const trimmed = to.trim()
  const isValid = recipientRegex.test(trimmed)
  const showError = touched && !isValid
  const isSending = status.kind === 'sending'
  const canSend = isValid && !isSending

  useEffect(() => {
    if (isValid) localStorage.setItem(storageKey, trimmed)
  }, [isValid, trimmed, storageKey])

  const handleSend = async () => {
    setTouched(true)
    if (!isValid) return
    setStatus({ kind: 'sending' })
    try {
      const outcome = await onSend({ to: trimmed, body })
      setStatus({ kind: 'success', outcome })
    } catch (err) {
      const message =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ||
        (err instanceof Error ? err.message : `Failed to send ${buttonLabel.toLowerCase()}`)
      setStatus({ kind: 'error', message })
    }
  }

  return (
    <div className="space-y-4 max-w-xl">
      <div>
        <label htmlFor={`tsf-to-${storageKey}`} className="block text-sm font-medium text-gray-700 mb-1">
          {recipientLabel}
        </label>
        <input
          id={`tsf-to-${storageKey}`}
          type={recipientType}
          value={to}
          onChange={(e) => setTo(e.target.value)}
          onBlur={() => setTouched(true)}
          placeholder={recipientPlaceholder}
          disabled={isSending}
          className={cn(
            'block w-full rounded-md border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2',
            showError
              ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
              : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500',
            isSending && 'bg-gray-50 cursor-not-allowed'
          )}
        />
        {showError && <p className="mt-1 text-xs text-red-600">{recipientInvalidMessage}</p>}
      </div>

      <div>
        <label htmlFor={`tsf-body-${storageKey}`} className="block text-sm font-medium text-gray-700 mb-1">
          Message body
        </label>
        <textarea
          id={`tsf-body-${storageKey}`}
          rows={bodyRows}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          disabled={isSending}
          className={cn(
            'block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500',
            isSending && 'bg-gray-50 cursor-not-allowed'
          )}
        />
        {bodyHelper && <p className="mt-1 text-xs text-gray-500">{bodyHelper}</p>}
      </div>

      <div>
        <button
          type="button"
          onClick={handleSend}
          disabled={!canSend}
          className={cn(
            'inline-flex items-center gap-2 rounded-md border border-transparent px-4 py-2 text-sm font-medium text-white shadow-sm',
            canSend
              ? 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
              : 'bg-blue-300 cursor-not-allowed'
          )}
        >
          <FontAwesomeIcon
            icon={isSending ? faSpinner : buttonIcon}
            className={cn('h-4 w-4', isSending && 'animate-spin')}
          />
          {isSending ? 'Sending…' : buttonLabel}
        </button>
      </div>

      {status.kind === 'success' && (
        <div className="max-w-xl flex items-start gap-2 rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-800">
          <FontAwesomeIcon icon={faCircleCheck} className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium">
              {mode === 'direct'
                ? `Sent to ${status.outcome.recipient}.`
                : `Alert row inserted for ${status.outcome.recipient}.`}
            </p>
            <p className="text-green-700">
              {mode === 'direct'
                ? 'Check the inbox (and spam folder).'
                : status.outcome.detail || 'The processor polls every 30 seconds. Check the log shortly.'}
            </p>
          </div>
        </div>
      )}

      {status.kind === 'error' && (
        <div className="max-w-xl flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          <FontAwesomeIcon icon={faCircleXmark} className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium">Failed to send.</p>
            <p className="text-red-700">{status.message}</p>
          </div>
        </div>
      )}
    </div>
  )
}

export function ModeBadge({ mode }: { mode: SendMode }) {
  if (mode === 'direct') {
    return (
      <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700 border border-gray-300">
        Direct
      </span>
    )
  }
  return (
    <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800 border border-amber-300">
      Pipeline
    </span>
  )
}

export function TestPageHeader({
  mode,
  title,
  subtitle,
}: {
  mode: SendMode
  title: string
  subtitle: React.ReactNode
}) {
  return (
    <div className="border-b border-gray-200 pb-4">
      <div className="flex items-center gap-3">
        <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
        <ModeBadge mode={mode} />
      </div>
      <p className="mt-1 text-sm text-gray-600">{subtitle}</p>
    </div>
  )
}
